<template>
    <div ref="root">
        <div class="tooltip-wrapper">
            <Transition name="fade">
                <div ref="tooltip"
                     class="tooltip-content border"
                     v-show="tooltipVisible"
                     :style="{ zIndex: isInitalized ? 50 : -50 }"
                     :class="[
                         tooltipType,
                         instanceClasses,
                         {
                             'shadow-sm':!instanceClasses.includes('shadow'),
                             'p-2':!instanceClasses.includes('p-'),
                             'bg-dark text-light-light':!instanceClasses.includes('bg-'),
                             'rounded':!instanceClasses.includes('rounded'),
                             'border-dark':!instanceClasses.includes('border-')
                         }
                     ]"
                     @mouseenter="abortHide"
                     @mouseleave="hideTooltip"
                >
                    <slot/>
                </div>
            </Transition>
        </div>
        <div ref="trigger"
             class="tooltip-trigger"
             @mouseenter="showTooltip"
             @mouseleave="hideTooltip"
        >
            <slot name="trigger"/>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        disabled: Boolean,
        offset: {
            type: Number,
            default: 10,
        },
        position: {
            type: String,
            default: 'top',
        },
    },

    data() {
        return {
            tooltipVisible: true, //Needs to be true on mount for content size to be determined by browser.
            pendingHide: null,
            timeout: 120,
            isInitalized: false,
        };
    },

    mounted() {
        this.initializeTooltipPosition();
    },

    methods: {
        initializeTooltipPosition() {
            //Overwrite root node class so we can pass it to tooltip element.
            this.$refs.root.className = ['d-flex align-items-center', this.flexDirection].join(' ');

            //Align tooltip
            const contentBoundaries = this.$refs.tooltip.getBoundingClientRect();

            if (this.position === 'top') {
                this.$refs.tooltip.style.top = (contentBoundaries.height * -1.0 - this.offset) + 'px';
            } else if (this.position === 'left' || this.position === 'right') {
                this.$refs.tooltip.style.top = (contentBoundaries.height * -0.5) + 'px';
            } else if (this.position === 'bottom') {
                this.$refs.tooltip.style.top = (this.offset) + 'px';
            }

            if (this.position === 'top' || this.position === 'bottom') {
                this.$refs.tooltip.style.left = (contentBoundaries.width * -0.5) + 'px';
            } else if (this.position === 'left') {
                this.$refs.tooltip.style.left = (contentBoundaries.width * -1.0 - this.offset * 2.0) + 'px';
            } else if (this.position === 'right') {
                this.$refs.tooltip.style.left = (this.offset * 2.0) + 'px';
            }

            // Hide tooltip
            this.$nextTick(() => {
                this.tooltipVisible = false;

                setTimeout(() => {
                    this.isInitalized = true;
                }, 100);
            });
        },

        showTooltip() {
            if (this.disabled || !this.tooltipHasContent) {
                return;
            }

            this.abortHide();
            this.tooltipVisible = true;
        },

        hideTooltip() {
            this.pendingHide = setTimeout(() => {
                this.tooltipVisible = false;
                this.pendingHide = null;
            }, this.timeout);
        },

        abortHide() {
            clearTimeout(this.pendingHide);
            this.pendingHide = null;
        },
    },

    computed: {
        instanceClasses() {
            return this.$options._parentVnode.data.staticClass || '';
        },

        tooltipHasContent() {
            return this.$refs.tooltip.innerText.trim() !== '';
        },

        flexDirection() {
            switch (this.position) {
                case 'left':
                    return 'flex-row';
                case 'right':
                    return 'flex-row-reverse';
                case 'bottom':
                    return 'flex-column-reverse';
                default:
                    return 'flex-column';
            }
        },

        tooltipType() {
            switch (this.position) {
                case 'left':
                    return 'tooltip-left';
                case 'right':
                    return 'tooltip-right';
                case 'bottom':
                    return 'tooltip-bottom';
                default:
                    return 'tooltip-top';
            }
        },
    },
};
</script>

<style>
.tooltip-wrapper {
    position: relative;
    width: 0;
    height: 0;
}

.tooltip-trigger {
    width: 100%;
}

.tooltip-content {
    position: absolute;
}

.tooltip-content::after {
    content: "";
    position: absolute;
    border-width: 8px;
    border-style: solid;
    border-color: transparent;
    border-top-color: inherit;
}

.tooltip-top::after {
    top: 100%;
    left: 50%;
}

.tooltip-right::after {
    top: 50%;
    left: -16px;
    transform: rotateZ(90deg);
}

.tooltip-bottom::after {
    top: -16px;
    left: 50%;
    transform: rotateZ(180deg);
}

.tooltip-left::after {
    top: 50%;
    left: 100%;
    transform: rotateZ(-90deg);
}

.fade-enter-active {
    transition: .24s ease-out;
}

.fade-leave-active {
    transition: .12s ease-in;
}

.fade-enter, .fade-leave-to {
    opacity: 0;
}
</style>
